/**
 * External Identity service.
 * Thin wrapper around /api/me/external-identities used by the personal
 * settings page to list, link, and revoke channel identities.
 */

import { api } from "@/utils/api-client";

/** Verified external identity row. */
export interface ExternalIdentity {
  id: string;
  channel: string;
  externalId: string;
  externalUsername: string | null;
  verificationMethod: string;
  verifiedAt: string;
  createdAt: string;
}

/** Optional info about prior chats found under the linked external id. */
export interface OrphanInfo {
  orphanUserId: string;
  messageCount: number;
  chatCount: number;
}

export interface LinkResponse {
  identity: ExternalIdentity;
  orphan: OrphanInfo | null;
}

export interface PhoneOtpStartResponse {
  verificationId: string;
  expiresAt: string;
}

/** Telegram Login Widget callback payload (verbatim from telegram). */
export interface TelegramLoginPayload {
  id: number | string;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number | string;
  hash: string;
}

const externalIdentityService = {
  async list(): Promise<{ identities: ExternalIdentity[] }> {
    return api.get("/me/external-identities", { requiresAuth: true });
  },

  async revoke(identityId: string): Promise<void> {
    await api.delete(`/me/external-identities/${identityId}`, {
      requiresAuth: true,
    });
  },

  async linkTelegram(payload: TelegramLoginPayload): Promise<LinkResponse> {
    return api.post("/me/external-identities/telegram", payload, {
      requiresAuth: true,
      showErrorToast: true,
    });
  },

  /**
   * Begin the Microsoft Teams OAuth link. Tenant/Client come from the
   * workspace's Single Sign-On config. Returns the authorize URL plus a CSRF
   * `state` and an OIDC `nonce` the client holds until `finishTeamsOAuth`.
   */
  async startTeamsOAuth(): Promise<{
    authUrl: string;
    state: string;
    nonce: string;
    redirectUri: string;
  }> {
    return api.post("/me/teams/start", {}, { requiresAuth: true });
  },

  /**
   * Finish the Teams OAuth link — the popup relayed `code + state`, the
   * client verified `state`, and now POSTs the `code` (plus the `nonce` it
   * held) so the server can verify the id_token and store the AAD `oid`.
   */
  async finishTeamsOAuth(args: { code: string; nonce: string }): Promise<LinkResponse> {
    return api.post("/me/teams/complete", args, {
      requiresAuth: true,
      showErrorToast: true,
    });
  },

  async startSlackOAuth(): Promise<{ url: string; state: string }> {
    return api.get("/me/external-identities/slack/start", { requiresAuth: true });
  },

  async finishSlackOAuth(args: { code: string; state: string }): Promise<LinkResponse> {
    return api.post("/me/external-identities/slack/callback", args, {
      requiresAuth: true,
      showErrorToast: true,
    });
  },

  async startPhoneOtp(args: { phone: string }): Promise<PhoneOtpStartResponse> {
    return api.post("/me/external-identities/phone/start", args, {
      requiresAuth: true,
      showErrorToast: true,
    });
  },

  async verifyPhoneOtp(args: {
    verificationId: string;
    code: string;
  }): Promise<LinkResponse> {
    return api.post("/me/external-identities/phone/verify", args, {
      requiresAuth: true,
      showErrorToast: true,
    });
  },

  async mergePriorHistory(identityId: string): Promise<{ merged: OrphanInfo }> {
    return api.post(
      `/me/external-identities/${identityId}/merge-prior`,
      {},
      { requiresAuth: true, showErrorToast: true },
    );
  },
};

export { externalIdentityService };
